import { useEffect, useLayoutEffect, useState } from 'react';
import styled from '@emotion/styled';
import { NG_APP_MODULE_NAME, NG_APP_SELECTOR_ID } from '@hotjar/webapp/constants/angular';
import { logError } from '@hotjar/webapp/lib/logger';
import { loadWindowSettings } from '@hotjar/webapp/utils/load-window-settings';
import { Loader } from '@hotjar/hotsauce';
import { useLocation } from 'react-router-dom';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import type { NotifyFunctions } from '@hotjar/webapp/hooks/useNotify';
import { setAngularInjector } from '@hotjar/webapp/utils/angular-injector';
import { getPageType, getRouteConfigByPath } from '@hotjar/webapp/utils/routing';
import { WithGettingStartedSideBar } from '../Activation/GetStartedGuide';

const NG_APP_CONTAINER_ID = 'ng-app-container';

const FullPageLoaderContainer = styled.div`
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export interface AngularRef {
  injector: ng.auto.IInjectorService | null;
  root: HTMLElement | null;
  isInitialized: boolean;
}

const angularRef: AngularRef = {
  injector: null,
  root: null,
  isInitialized: false,
};

export const NgApp = (): JSX.Element => {
  // By default we only show the loading spinner if we have never bootstrapped the angular app.
  const [isLoading, setIsLoading] = useState(!angularRef.injector);
  const location = useLocation();
  const Notify = useNotify();

  const routeConfig = getRouteConfigByPath(location.pathname);
  const routeName = routeConfig?.name ?? '';
  const pageType = getPageType(routeName);
  const isFeaturePage = pageType === 'feature';

  useEffect(() => {
    if (angularRef.injector) {
      const $timeout = angularRef.injector.get('$timeout');

      // We need to trigger a digest but aren't sure if there is already one in progress so we use $timeout
      // instead which will schedule a digest when there isn't one already happening.
      const timeoutPromise = $timeout(() => null);

      return () => {
        $timeout.cancel(timeoutPromise);
      };
    }

    return () => null;
  }, [location]);

  useLayoutEffect(() => {
    const appElement = document.getElementById(NG_APP_SELECTOR_ID);
    if (!appElement) {
      logError(
        new Error('No app element found to bootstrap Angular application within React app.'),
      );

      return;
    }

    if (!angularRef.injector) {
      // Don't bootstrap if we've bootstrapped before
      if (angularRef.isInitialized) {
        return;
      }

      angularRef.isInitialized = true;

      // The angular app still relies on global window settings to bootstrap.
      loadWindowSettings()
        .then(() => {
          window.angular.element(document).ready(() => {
            // Once the angular app is running we can hide the loading spinner.
            window.angular
              .module(NG_APP_MODULE_NAME)
              .config([
                'UseNotifyProvider',
                (UseNotifyProvider: { provide: (hook: NotifyFunctions) => void }) => {
                  UseNotifyProvider.provide(Notify);
                },
              ])
              .run([
                '$injector',
                ($injector: ng.auto.IInjectorService) => {
                  /* Store a reference to the injector that can be used in bridging hooks `getAngularService` call.
                   * This avoids a bug where the we can't access the injector through `angular.element('....').injector()`
                   * when moving to non-angular pages.
                   * We do this during the run block to ensure we get access to the injector as soon as its available.
                   * Reference bug: https://hotjar.atlassian.net/browse/EE-1742
                   */
                  setAngularInjector($injector);

                  setIsLoading(false);
                },
              ]);

            // Bootstrap the app and keep a reference to the app injector that we can use later to access $rootScope.
            const injector = window.angular.bootstrap(appElement, [NG_APP_MODULE_NAME]);

            /**
             * We keep a reference to the root and injector of the angular app so that if this `NgApp` component gets
             * unmounted, and remounted we can restore the already running angular app by just reinserting it into the
             * DOM. This avoids having to re-bootstrap the entire application every time we switch to the angular app
             * after the initial bootstrap.
             */

            /* eslint-disable no-param-reassign */
            angularRef.injector = injector;
            /* eslint-disable no-param-reassign */
            angularRef.root = appElement;

            const appContainer = document.querySelector(`#${NG_APP_CONTAINER_ID}`);
            if (!appContainer) {
              return;
            }

            appContainer.innerHTML = '';
            appContainer.appendChild(appElement);
          });
        })
        // TODO: Revisit this and add an error view for the user to indicate the app couldn't load properly.
        // This may be less relevant once the nav is in React since the user will see something.
        // For now this mimics the behaviour users currently experience if settings fails to load. A look in
        // Sentry indicates this only happens about 30 times a day across all sessions.
        .catch(() => setIsLoading(false));
    } else if (angularRef.injector && angularRef.root) {
      const appContainer = document.querySelector(`#${NG_APP_CONTAINER_ID}`);
      if (!appContainer) {
        return;
      }

      // Remove the `<app-component>` html since we're inserting an already running angular app in place.
      appContainer.innerHTML = '';
      // Digest the app now so that its rendering the correct route when the DOM is appended.
      angularRef.injector.get('$rootScope').$applyAsync();

      // Re-insert the running angular app into the DOM since we have already bootstrapped it in the past.
      appContainer.appendChild(angularRef.root);
    } else {
      logError(
        new Error(
          `Something went wrong and we didn't bootstrap or have an already bootstrapped app.`,
        ),
      );
    }
  }, [Notify]);

  return (
    <>
      {/* Show a loader while we wait for the angular app to bootstrap. It's better than an empty screen. */}
      {isLoading && (
        <FullPageLoaderContainer>
          <Loader size="xl" />
        </FullPageLoaderContainer>
      )}
      {isFeaturePage ? (
        <WithGettingStartedSideBar>
          {/* We use this container to be able to reinsert an existing running angular app. */}
          <div id={NG_APP_CONTAINER_ID}>
            <div id={NG_APP_SELECTOR_ID} className="hotjarThemeContainer">
              <app-component />
            </div>
          </div>
        </WithGettingStartedSideBar>
      ) : (
        <div id={NG_APP_CONTAINER_ID}>
          <div id={NG_APP_SELECTOR_ID} className="hotjarThemeContainer">
            <app-component />
          </div>
        </div>
      )}
    </>
  );
};
